import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import VerticalCylinderTankWidget from "../../../Widgets/LiquidLevel/Widgets/VerticalCylinderTankWidget";//" ./VerticalCylinderTankWidget";
import "../../../../styles/workspace_styles/CustomerAdministratorStyles/Dashboards/DashboardState.css";
import { DashboardContext } from "./DashboardProvider";
import LineChartWidget from "../../../Widgets/Charts/Widgets/LineChartWidget";
import BarChartWidget from "../../../Widgets/Charts/Widgets/BarChartWidget";
import StateNavigationButtonWidget from "../../../Widgets/Buttons/widgets/StateNavigationButtonWidget";
import RadialGaugeWidget from "../../../Widgets/AnalogueGauges/widgets/RadialGaugeWidget";

const DashboardState = () => {
    const NUM_COLS = 20;
    const NUM_ROWS = 20;
    const COL_GAP = 2;
    const ROW_GAP = 2;
    const MOBILE_COMPUTER_SCREEN_BOUNDARY = 950;
    const containerRef = useRef(null);
    const scrollWidgetsRef = useRef({});
    const widgetsUpdatingRef = useRef(false);
    const {editMode,setDashboard,activeDashboardState,setActiveDashboardState,activeEditWidget,refreshDashboardState} = useContext(DashboardContext);
    const [widgets,setWidgets] = useState([]);
    const [draggingOrResizing, setDraggingOrResizing] = useState(true);
    const [bounds, setBounds] = useState({ width: 0, height: 0, gridCellSize: 50, numCols: NUM_COLS });
    const [computerScreenWidgets,setComputerScreenWidgets] = useState([]);
    const [mobileScreenWidgets,setMobileScreenWidgets] = useState([]);
    const sortedWidgets = useMemo(() => {
        return [...widgets].sort((a, b) => {
        if (a.row === b.row) return a.col - b.col;
        return a.row - b.row;
        });
    },[widgets]);
    useEffect(() =>{
        if(activeDashboardState && widgetsUpdatingRef.current){
            widgetsUpdatingRef.current = false;
            setDashboard(prev => {
                if(!prev || !prev.dashboardStates) return prev;
                return{...prev,dashboardStates:prev.dashboardStates.map(ds => ds.id === activeDashboardState?.id? activeDashboardState:ds)};
            });
        }
        else if(activeDashboardState) setWidgets(activeDashboardState.widgets || []);
    },[activeDashboardState]);
    useEffect(() =>{
        if (bounds.width < MOBILE_COMPUTER_SCREEN_BOUNDARY) {
            let newWidgets = [];
            let currentRow = 0;
            
            sortedWidgets.forEach((widget) => {
                newWidgets.push({
                    ...widget,
                    row: currentRow,
                    col: 0,
                    sizeX: NUM_COLS,
                    sizeY: widget.sizeY * widget.mobileHeightFactor
                });
                currentRow += widget.sizeY * widget.mobileHeightFactor;  // Increment `currentRow` based on the adjusted `sizeY`
            });
            setMobileScreenWidgets(newWidgets);
            setComputerScreenWidgets(null);
        } else {
            setComputerScreenWidgets(sortedWidgets);
            setMobileScreenWidgets(null);
        }
    },[bounds.width,sortedWidgets]);
    useEffect(() =>{
        if(!activeDashboardState) return;
        const timeoutId = setTimeout(() => {
            widgetsUpdatingRef.current = true;
            setActiveDashboardState(prev => ({ ...prev, widgets }));
        }, 400);
        return () => clearTimeout(timeoutId);
    },[widgets]);
    useEffect(() =>{
        const scrollContainer = document.querySelector('.tenant-admin-dashboard-state-container');
        if(bounds.width > MOBILE_COMPUTER_SCREEN_BOUNDARY){
            scrollContainer?.scrollTo({top:0,behavior:'smooth'});
            return;
        }
        if(!activeEditWidget) return;
        const timeout = setTimeout(() =>{
            const target = scrollWidgetsRef.current[activeEditWidget?.id]?.querySelector('.general-widget-parent-container');
            if (scrollContainer && target) {
                const topValue = parseFloat(target.style.top);
                const scrollOffset = topValue - scrollContainer.clientHeight / 2.6 + target.clientHeight / 2;
                scrollContainer.scrollTo({
                    top: scrollOffset,
                    behavior: 'smooth'
                });
            }
        },50);
        return() => clearTimeout(timeout);
    },[activeEditWidget,bounds.width]);
    useLayoutEffect(() => {
        const updateBounds = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth;
                const height = containerRef.current.clientHeight;
                const gridSizeX = Math.floor(width / NUM_COLS);
                const gridSizeY = Math.floor(height / NUM_ROWS);
                const gridCellSize = Math.min(gridSizeX, gridSizeY);
                setBounds({ width, height, gridCellSize, colGap: COL_GAP, rowGap: ROW_GAP, NUM_COLS });
            }
        };
        updateBounds();
        window.addEventListener("resize", updateBounds);
        return () => window.removeEventListener("resize", updateBounds);
    }, [bounds.width,activeEditWidget]);
    return (
        <div className="customer-admin-dashboard-state">
            <div className="customer-admin-dashboard-state-header"
                style={{display: editMode || activeDashboardState?.showTitle? 'block':'none'}}>
                <p style={{display: editMode? 'block':'none'}}>Title*</p>
                <h1 style={{display:activeDashboardState?.showTitle? 'block':'none'}}>{activeDashboardState?.name}</h1>
            </div>
            <div className="customer-admin-dashboard-body"
                ref={containerRef}
                style={{
                    backgroundSize: draggingOrResizing? `${bounds.gridCellSize + bounds.colGap}px ${bounds.gridCellSize + bounds.rowGap}px`: "auto",
                    backgroundImage: draggingOrResizing? "linear-gradient(to right, lightgray 1px, transparent 1px), linear-gradient(to bottom, lightgray 1px, transparent 1px)":'none',
                }}>
                <div>
                    {
                        bounds.width >= MOBILE_COMPUTER_SCREEN_BOUNDARY? (
                            <div>
                                {computerScreenWidgets && computerScreenWidgets.map((widget) =>{
                                    switch(widget?.type){
                                        case 'vertical_cylinder_tank':
                                            return (
                                                <VerticalCylinderTankWidget
                                                    key={!refreshDashboardState.current? widget.id:`${widget.id} ${widget?.col} ${widget?.row}`}
                                                    DashboardContext={DashboardContext}
                                                    widget={widget}
                                                    setWidgets={setWidgets}
                                                    bounds={bounds}
                                                    setDraggingOrResizing={setDraggingOrResizing}
                                                    MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                />
                                            );
                                        case 'line_chart':
                                            return (
                                                <LineChartWidget
                                                    key={!refreshDashboardState.current? widget.id:`${widget.id} ${widget?.col} ${widget?.row}`}
                                                    DashboardContext={DashboardContext}
                                                    widget={widget}
                                                    setWidgets={setWidgets}
                                                    bounds={bounds}
                                                    setDraggingOrResizing={setDraggingOrResizing}
                                                    MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                />
                                            );
                                        case 'bar_chart':
                                            return (
                                                <BarChartWidget
                                                    key={!refreshDashboardState.current? widget.id:`${widget.id} ${widget?.col} ${widget?.row}`}
                                                    DashboardContext={DashboardContext}
                                                    widget={widget}
                                                    setWidgets={setWidgets}
                                                    bounds={bounds}
                                                    setDraggingOrResizing={setDraggingOrResizing}
                                                    MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                />
                                            );
                                        case 'radial_gauge':
                                            return(
                                                <RadialGaugeWidget
                                                    key={!refreshDashboardState.current? widget.id:`${widget.id} ${widget?.col} ${widget?.row}`}
                                                    DashboardContext={DashboardContext}
                                                    widget={widget}
                                                    setWidgets={setWidgets}
                                                    bounds={bounds}
                                                    setDraggingOrResizing={setDraggingOrResizing}
                                                    MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                />
                                            );
                                        case 'state_navigation_button':
                                            return(
                                                <StateNavigationButtonWidget
                                                    key={!refreshDashboardState.current? widget.id:`${widget.id} ${widget?.col} ${widget?.row}`}
                                                    DashboardContext={DashboardContext}
                                                    widget={widget}
                                                    setWidgets={setWidgets}
                                                    bounds={bounds}
                                                    setDraggingOrResizing={setDraggingOrResizing}
                                                    MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                />
                                            );
                                    }
                                })}
                            </div>
                        ):(
                            <div>
                                {mobileScreenWidgets && mobileScreenWidgets.map((widget) =>{
                                    switch(widget?.type){
                                        case 'vertical_cylinder_tank':
                                            return (
                                                <div key={widget?.id} ref={(el) => (scrollWidgetsRef.current[widget?.id] = el)}>
                                                    <VerticalCylinderTankWidget
                                                        key={widget.id}
                                                        DashboardContext={DashboardContext}
                                                        widget={widget}
                                                        setWidgets={setWidgets}
                                                        bounds={bounds}
                                                        setDraggingOrResizing={setDraggingOrResizing}
                                                        MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                    />
                                                </div>
                                            );
                                        case 'line_chart':
                                            return (
                                                <div key={widget?.id} ref={(el) => (scrollWidgetsRef.current[widget?.id] = el)}>
                                                    <LineChartWidget
                                                        key={!refreshDashboardState.current? widget.id:`${widget.id} ${widget?.col} ${widget?.row}`}
                                                        DashboardContext={DashboardContext}
                                                        widget={widget}
                                                        setWidgets={setWidgets}
                                                        bounds={bounds}
                                                        setDraggingOrResizing={setDraggingOrResizing}
                                                        MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                    />
                                                </div>
                                            );
                                        case 'bar_chart':
                                            return (
                                                <div key={widget?.id} ref={(el) => (scrollWidgetsRef.current[widget?.id] = el)}>
                                                    <BarChartWidget
                                                        key={!refreshDashboardState.current? widget.id:`${widget.id} ${widget?.col} ${widget?.row}`}
                                                        DashboardContext={DashboardContext}
                                                        widget={widget}
                                                        setWidgets={setWidgets}
                                                        bounds={bounds}
                                                        setDraggingOrResizing={setDraggingOrResizing}
                                                        MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                    />
                                                </div>
                                            );
                                        case 'radial_gauge':
                                            return(
                                                <div key={widget?.id} ref={(el) => (scrollWidgetsRef.current[widget?.id] = el)}>
                                                    <RadialGaugeWidget
                                                        key={!refreshDashboardState.current? widget.id:`${widget.id} ${widget?.col} ${widget?.row}`}
                                                        DashboardContext={DashboardContext}
                                                        widget={widget}
                                                        setWidgets={setWidgets}
                                                        bounds={bounds}
                                                        setDraggingOrResizing={setDraggingOrResizing}
                                                        MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                    />
                                                </div>
                                            );
                                        case 'state_navigation_button':
                                            return(
                                                <div key={widget?.id} ref={(el) => (scrollWidgetsRef.current[widget?.id] = el)}>
                                                    <StateNavigationButtonWidget
                                                        key={widget?.id}
                                                        DashboardContext={DashboardContext}
                                                        widget={widget}
                                                        setWidgets={setWidgets}
                                                        bounds={bounds}
                                                        setDraggingOrResizing={setDraggingOrResizing}
                                                        MOBILE_COMPUTER_SCREEN_BOUNDARY={MOBILE_COMPUTER_SCREEN_BOUNDARY}
                                                    />
                                                </div>
                                            );
                                    }
                                })}
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

export default DashboardState;